<template>

  <modalo
    :title="$t('Template preview')"
    type="mini"
    @hideModal="$emit('close')">

    <div class="template-preview">

      <div class="d-flex justify-content-between align-items-center mb-2">
        <strong>{{ template.name }}</strong>
        <span class="badge badge-light">{{ template.category }} · {{ template.language }}</span>
      </div>

      <!-- variable inputs -->
      <div
        v-if="placeholders.length"
        class="form-group">
        <label class="small text-muted">{{ $t('Variables') }}</label>
        <div
          v-for="placeholder in placeholders"
          :key="placeholder"
          class="input-group input-group-sm mb-2">
          <div class="input-group-prepend">
            <span class="input-group-text">{{ wrapPlaceholder(placeholder) }}</span>
          </div>
          <input
            v-model="values[placeholder]"
            type="text"
            class="form-control">
        </div>
      </div>

      <!-- final preview -->
      <label class="small text-muted">{{ $t('Preview') }}</label>
      <div class="template-preview-box border rounded bg-light p-2">{{ finalText }}</div>

    </div>

    <template slot="footer">
      <button
        type="button"
        class="btn btn-light mr-2"
        :disabled="sending"
        @click="$emit('close')">{{ $t('Cancel') }}</button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="sending || !isComplete"
        @click="confirm">
        <i
          v-if="sending"
          class="fa-solid fa-circle-notch fa-spin mr-1"></i>
        {{ $t('Send') }}
      </button>
    </template>

  </modalo>

</template>

<script>
export default {
  name: 'WhatsappTemplatePreview',
  props: {
    template: {
      type: Object,
      required: true
    },
    sending: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      values: {}
    }
  },
  computed: {
    bodyComponent () {
      return (this.template.components || []).find((component) => { return component.type === 'BODY' }) || {}
    },
    bodyText () {
      return this.bodyComponent.text || ''
    },
    // ordered, unique placeholders found in the body text ({{1}}, {{2}}, ...)
    placeholders () {
      const matches = this.bodyText.match(/{{\s*([^}]+?)\s*}}/g) || []
      const unique = []
      matches.forEach((match) => {
        const key = match.replace(/{{\s*|\s*}}/g, '')
        if (!unique.includes(key)) {
          unique.push(key)
        }
      })
      return unique
    },
    finalText () {
      let text = this.bodyText
      this.placeholders.forEach((placeholder) => {
        const value = this.values[placeholder] || ('{{' + placeholder + '}}')
        text = text.split('{{' + placeholder + '}}').join(value)
      })
      return text
    },
    isComplete () {
      return this.placeholders.every((placeholder) => {
        return !!(this.values[placeholder] && this.values[placeholder].trim())
      })
    }
  },
  created () {
    // initialize a reactive slot for each placeholder
    const values = {}
    this.placeholders.forEach((placeholder) => { values[placeholder] = '' })
    this.values = values
  },
  methods: {
    /*
    wrapPlaceholder
    renders a placeholder token (kept in a method to avoid mustache parsing issues)
    */
    wrapPlaceholder (placeholder) {
      return '{{' + placeholder + '}}'
    },
    /*
    confirm
    builds the WhatsApp template payload (positional body parameters)
    */
    confirm () {
      if (!this.isComplete && this.placeholders.length) {
        return
      }

      const payload = {
        name: this.template.name,
        language: this.template.language,
        // kept for the optimistic bubble preview
        body: this.finalText
      }

      if (this.placeholders.length) {
        payload.components = [{
          type: 'body',
          parameters: this.placeholders.map((placeholder) => {
            return { type: 'text', text: this.values[placeholder] }
          })
        }]
      }

      this.$emit('confirm', payload)
    }
  }
}
</script>

<style scoped>
/* wrap long text and preserve the template's line breaks */
.template-preview-box {
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/composer/WhatsappTemplatePreview.vue